import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Sidebar.css";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  ManageSearch,
  Warehouse,
  DataUsage,
  ContactPage,
  ContentPasteSearch,
  CreditScore,
  Upload as UploadIcon,
  ExpandLess,
  ExpandMore,
  Equalizer,
  FindInPage,
  LocalShipping,
  Receipt,
  NewReleases,
  QrCodeScanner,
  Menu as MenuIcon,
  DesignServices as DesignServicesIcon,
  CreateNewFolder as CreateNewFolderIcon,
  DocumentScanner as DocumentScannerIcon,
  Folder as FolderIcon,
  LibraryBooks as LibraryBooksIcon,
  Book as BookIcon,
  PeopleAlt as PeopleAltIcon,
  Engineering as EngineeringIcon,
  CallReceived as CallReceivedIcon,
  Summarize as SummarizeIcon,
  HeartBroken,
} from "@mui/icons-material";

const Sidebar = ({ sidebarOpen, toggleSidebar }) => {
  const [mobileView, setMobileView] = useState(false);
  const [menus, setMenus] = useState([
    { title: "Workspace", route: "/", icon: <HomeIcon /> },
    { title: "Process Flow Chart", route: "/process-flow-chart", icon: <AssessmentIcon /> },
    { title: "Management Summary", route: "/management-summary", icon: <ManageSearch /> },
    { title: "Receiving Modules", route: "/receiving-modules", icon: <LocalShipping /> },
    {
      title: "Manufacturing Traceability",
      route: "/manufacturing-traceability",
      icon: <Warehouse />,
      showSubMenu: false,
      submenus: [
        { title: "Upload", route: "/manufacturing-traceability/upload", icon: <UploadIcon /> },
        { title: "PDI Verification", route: "/manufacturing-traceability/tracking", icon: <SearchIcon /> },
        { title: "Traceability Search", route: "/manufacturing-traceability/search", icon: <FindInPage /> },
        { title: "Shipment Tracker", route: "/manufacturing-traceability/shipment-tracker", icon: <DataUsage /> },
        { title: "PDI Witness", route: "/manufacturing-traceability/pdi-witness", icon: <CreditScore /> },
      ],
    },
    { title: "Inspections", route: "/inspections", icon: <ContentPasteSearch /> },
    {
      title: "Quality Inspection",
      route: "/quality-inspection-summary",
      icon: <SummarizeIcon />,
      showSubMenu: false,
      submenus: [
        { title: "Summary", route: "/quality-inspection-summary", icon: <SummarizeIcon /> },
        { title: "Graphical Summary", route: "/graphical-summary", icon: <Equalizer /> },
      ],
    },
    {
      title: "Table-wise Scanning",
      route: "/table-wise-scanning",
      icon: <QrCodeScanner />,
      showSubMenu: false,
      submenus: [
        { title: "Design Inputs", route: "/table-wise-scanning/design-inputs", icon: <DesignServicesIcon /> },
        { title: "Site Planner Inputs", route: "/table-wise-scanning/site-planner-inputs", icon: <CreateNewFolderIcon /> },
        { title: "Site Supervisor Inputs", route: "/table-wise-scanning/site-supervisor-inputs", icon: <DocumentScannerIcon /> },
      ],
    },
    {
      title: "Support Documents",
      route: "/support-documents",
      icon: <FolderIcon />,
      showSubMenu: false,
      submenus: [
        { title: "Quality", route: "/support-documents/quality", icon: <Equalizer /> },
        { title: "Engineering", route: "/support-documents/engineering", icon: <EngineeringIcon /> },
        { title: "Receiving", route: "/support-documents/receiving", icon: <CallReceivedIcon /> },
        { title: "Open for All", route: "/support-documents/open-for-all", icon: <PeopleAltIcon /> },
      ],
    },
    {
      title: "Module Reconciliation",
      route: "/module-reconciliation-sheet",
      icon: <ContactPage />,
      showSubMenu: false,
      submenus: [
        { title: "Dashboard", route: "/module-reconciliation-sheet/module-reconciliation-dashboard", icon: <FolderIcon /> },
        { title: "Damaged Module Information", route: "/module-reconciliation-sheet/module-reconciliation-Damaged", icon: <HeartBroken /> },
        { title: "Issue & Return", route: "/module-reconciliation-sheet/module-reconciliation-Store", icon: <BookIcon /> },
        { title: "Unaccounted Modules", route: "/module-reconciliation-sheet/module-reconciliation-Unaccounted", icon: <LibraryBooksIcon /> },
      ],
    },
    { title: "Automated Report", route: "/automated-report", icon: <Receipt />,
      showSubMenu: false,
      submenus: [
        { title: "Report Form", route: "/automated-report/report-form", icon: <Receipt /> },
        { title: "Report Tracker", route: "/automated-report/report-tracker", icon: <SearchIcon /> },
      ]
     },
    { title: "Release Notes Phase-1.0.0", route: "/releasenotes", icon: <NewReleases /> },
  ]);

  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);

  // User menu restrictions configuration
  const userMenuRestrictions = [
    {
      username: "ul.susten",
      hiddenMenus: [
        "Process Flow Chart",
        "Automated Report",
        "Workspace",
        "Management Summary",
        "Receiving Modules",
        "Table-wise Scanning",
        "Support Documents",
        "Module Reconciliation",
        "Release Notes Phase-1.0.0",
      ],
    },
    {
      username: "waaree.susten",
      hiddenMenus: [
        "Process Flow Chart",
        "Automated Report",
        "Workspace",
        "Management Summary",
        "Receiving Modules",
        "Quality Inspection",
        "Table-wise Scanning",
        "Support Documents",
        "Module Reconciliation",
        "Release Notes Phase-1.0.0",
      ],
    },
    {
      username: "rayzon.susten",
      hiddenMenus: [
        "Process Flow Chart",
        "Automated Report",
        "Workspace",
        "Management Summary",
        "Receiving Modules",
        "Quality Inspection",
        "Table-wise Scanning",
        "Support Documents",
        "Module Reconciliation",
        "Release Notes Phase-1.0.0",
        "Inspections",
        "Quality",
      ],
    }
  ];

  useEffect(() => {
    // Get username from sessionStorage
    const username = sessionStorage.getItem("username");
    setCurrentUser(username);

    checkMobileView();
    window.addEventListener("resize", checkMobileView);
    return () => {
      window.removeEventListener("resize", checkMobileView);
    };
  }, []);

  const checkMobileView = () => {
    setMobileView(window.innerWidth <= 760);
    if (window.innerWidth <= 760) toggleSidebar(false);
  };

  const isCurrentRoute = (route) => location.pathname.includes(route);

  const navigateTo = (route) => {
    navigate(route);
    if (mobileView) toggleSidebar(false);
  };

  const toggleSubMenu = (menuTitle) => {
    setMenus((prevMenus) =>
      prevMenus.map((menu) =>
        menu.title === menuTitle ? { ...menu, showSubMenu: !menu.showSubMenu } : menu
      )
    );
  };

  const closeAllSubMenus = () => {
    setMenus((prevMenus) =>
      prevMenus.map((menu) => ({
        ...menu,
        showSubMenu: false,
      }))
    );
  };

  const handleToggleSidebar = () => {
    if (sidebarOpen) {
      closeAllSubMenus();
    }
    toggleSidebar(!sidebarOpen);
  };

  const isSubMenuSelected = (menu) => {
    if (menu.submenus) {
      return menu.submenus.some((submenu) => location.pathname === submenu.route);
    }
    return false;
  };

  // Filter menus based on user restrictions
  const getFilteredMenus = () => {
    if (!currentUser) return menus;

    const userRestrictions = userMenuRestrictions.find(
      (user) => user.username === currentUser
    );

    if (!userRestrictions) return menus;

    return menus.filter(
      (menu) => !userRestrictions.hiddenMenus.includes(menu.title)
    );
  };

  const filteredMenus = getFilteredMenus();

  return (
    <Drawer
      variant={mobileView ? "temporary" : "permanent"}
      open={sidebarOpen}
      onClose={handleToggleSidebar}
      sx={{
        "& .MuiDrawer-paper": {
          width: sidebarOpen ? 300 : 60,
          backgroundColor: "#111827",
          color: "white",
          transition: "width 0.3s",
        },
      }}
    >
      <div className="flex justify-end p-2">
        <IconButton onClick={handleToggleSidebar}>
          <MenuIcon style={{ color: "white" }} />
        </IconButton>
      </div>
      <List className="hide-scrollbar">
        {filteredMenus.map((menu, menuIndex) => (
          <div key={menuIndex}>
            <ListItem
              button
              onClick={() => (menu.submenus ? toggleSubMenu(menu.title) : navigateTo(menu.route))}
              selected={isCurrentRoute(menu.route) || isSubMenuSelected(menu)}
              sx={{
                "&:hover": {
                  backgroundColor: "#2a2f3b",
                },
                backgroundColor: isCurrentRoute(menu.route) || isSubMenuSelected(menu) ? "#2a2f3b" : "",
              }}
            >
              <ListItemIcon sx={{ color: "white", minWidth: 55 }}>
                {menu.icon}
              </ListItemIcon>
              <ListItemText
                primary={menu.title}
                sx={{
                  opacity: sidebarOpen ? 1 : 0,
                  visibility: sidebarOpen ? "visible" : "hidden",
                  transition: "opacity 0.3s, visibility 0.3s",
                  width: sidebarOpen ? "auto" : 0,
                  whiteSpace: "nowrap",
                }}
              />
              {menu.submenus && (menu.showSubMenu ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>
            {menu.submenus && (
              <Collapse in={menu.showSubMenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding className="hide-scrollbar">
                  {menu.submenus.map((submenu, submenuIndex) => (
                    <ListItem
                      button
                      key={submenuIndex}
                      onClick={() => navigateTo(submenu.route)}
                      sx={{
                        pl: 4,
                        "&:hover": {
                          backgroundColor: "#2a2f3b",
                        },
                        backgroundColor: isCurrentRoute(submenu.route) ? "#2a2f3b" : "",
                      }}
                    >
                      <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
                        {submenu.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={submenu.title}
                        sx={{
                          opacity: sidebarOpen ? 1 : 0,
                          visibility: sidebarOpen ? "visible" : "hidden",
                          transition: "opacity 0.3s, visibility 0.3s",
                          width: sidebarOpen ? "auto" : 0,
                          whiteSpace: "nowrap",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </div>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;

// import React, { useState, useEffect } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import "./Sidebar.css";
// import {
//   Drawer,
//   List,
//   ListItem,
//   ListItemIcon,
//   ListItemText,
//   Collapse,
//   IconButton,
// } from "@mui/material";
// import {
//   Home as HomeIcon,
//   Assessment as AssessmentIcon,
//   Search as SearchIcon,
//   ManageSearch,
//   Warehouse,
//   DataUsage,
//   ContactPage,
//   ContentPasteSearch,
//   CreditScore,
//   Upload as UploadIcon,
//   ExpandLess,
//   ExpandMore,
//   Equalizer,
//   FindInPage,
//   LocalShipping,
//   Receipt,
//   NewReleases,
//   QrCodeScanner,
//   Menu as MenuIcon,
//   DesignServices as DesignServicesIcon,
//   CreateNewFolder as CreateNewFolderIcon,
//   DocumentScanner as DocumentScannerIcon,
//   Folder as FolderIcon,
//   LibraryBooks as LibraryBooksIcon,
//   Book as BookIcon,
//   PeopleAlt as PeopleAltIcon,
//   Engineering as EngineeringIcon,
//   CallReceived as CallReceivedIcon,
//   Summarize as SummarizeIcon,
//   Summarize,
//   HeartBroken,

// } from "@mui/icons-material";

// const Sidebar = ({ sidebarOpen, toggleSidebar }) => {
//   const [mobileView, setMobileView] = useState(false);
//   const [menus, setMenus] = useState([
//     { title: "Workspace", route: "/", icon: <HomeIcon /> },
//     { title: "Process Flow Chart", route: "/process-flow-chart", icon: <AssessmentIcon /> },
//     { title: "Management Summary", route: "/management-summary", icon: <ManageSearch /> },
//     { title: "Receiving Modules", route: "/receiving-modules", icon: <LocalShipping /> },
//     {
//       title: "Manufacturing Traceability",
//       route: "/manufacturing-traceability",
//       icon: <Warehouse />,
//       showSubMenu: false,
//       submenus: [
//         { title: "Upload", route: "/manufacturing-traceability/upload", icon: <UploadIcon /> },
//         { title: "PDI Verification", route: "/manufacturing-traceability/tracking", icon: <SearchIcon /> },
//         { title: "Traceability Search", route: "/manufacturing-traceability/search", icon: <FindInPage /> },
//         { title: "Shipment Tracker", route: "/manufacturing-traceability/shipment-tracker", icon: <DataUsage /> },
//         { title: "PDI Witness", route: "/manufacturing-traceability/pdi-witness", icon: <CreditScore /> },
//       ],
//     },
//     { title: "Inspections", route: "/inspections", icon: <ContentPasteSearch /> },
//     { title: "Quality Inspection", route: "/quality-inspection-summary", icon: <Summarize />,
//       showSubMenu: false,
//       submenus: [
//         { title: "Summary", route: "/quality-inspection-summary", icon: <Summarize /> },
//         { title: "Graphical Summary", route: "/graphical-summary", icon: <Equalizer /> },
//       ],
//     },

//     {
//       title: "Table-wise Scanning", route: "/table-wise-scanning", icon: <QrCodeScanner />,
//       showSubMenu: false,
//       submenus: [
//         { title: "Design Inputs", route: "/table-wise-scanning/design-inputs", icon: <DesignServicesIcon /> },
//         { title: "Site Planner Inputs", route: "/table-wise-scanning/site-planner-inputs", icon: <CreateNewFolderIcon /> },
//         { title: "Site Supervisor Inputs", route: "/table-wise-scanning/site-supervisor-inputs", icon: <DocumentScannerIcon /> },
//       ],
//     },
//     {
//       title: "Support Documents", route: "/support-documents", icon: <FolderIcon />,
//       showSubMenu: false,
//       submenus: [
//         { title: "Quality", route: "/support-documents/quality", icon: <Equalizer />},
//         { title: "Engineering", route: "/support-documents/engineering", icon: <EngineeringIcon /> },
//         { title: "Receiving", route: "/support-documents/receiving", icon: <CallReceivedIcon /> },
//         { title: "Open for All", route: "/support-documents/open-for-all", icon: <PeopleAltIcon /> },
//       ],
//     },
//     { 
//       title: "Module Reconciliation", route: "/module-reconciliation-sheet", icon: <ContactPage />,
//       showSubMenu: false,
//       submenus: [
//         { title: "Dashboard", route: "/module-reconciliation-sheet/module-reconciliation-dashboard", icon: <FolderIcon /> },
//         { title: "Damaged Module Information", route: "/module-reconciliation-sheet/module-reconciliation-Damaged", icon: <HeartBroken /> },
//         { title: "Return To Store", route: "/module-reconciliation-sheet/module-reconciliation-Store", icon: <BookIcon /> },
//         { title: "Unaccounted Modules", route: "/module-reconciliation-sheet/module-reconciliation-Unaccounted", icon: <LibraryBooksIcon /> },
//       ],  
//     },
//     { title: "Automated Report", route: "/ncr", icon: <Receipt /> },
//     { title: "Release Notes Phase-1.0.0", route: "/releasenotes", icon: <NewReleases /> },
//   ]);

//   const navigate = useNavigate();
//   const location = useLocation();

//   useEffect(() => {
//     checkMobileView();
//     window.addEventListener("resize", checkMobileView);
//     return () => {
//       window.removeEventListener("resize", checkMobileView);
//     };
//   }, []);

//   const checkMobileView = () => {
//     setMobileView(window.innerWidth <= 760);
//     if (window.innerWidth <= 760) toggleSidebar(false);
//   };

//   const isCurrentRoute = (route) => location.pathname.includes(route);

//   const navigateTo = (route) => {
//     navigate(route);
//     if (mobileView) toggleSidebar(false);
//   };

//   const toggleSubMenu = (menuIndex) => {
//     setMenus((prevMenus) =>
//       prevMenus.map((menu, index) =>
//         index === menuIndex ? { ...menu, showSubMenu: !menu.showSubMenu } : menu
//       )
//     );
//   };

//   const closeAllSubMenus = () => {
//     setMenus((prevMenus) =>
//       prevMenus.map((menu) => ({
//         ...menu,
//         showSubMenu: false,
//       }))
//     );
//   };

//   const handleToggleSidebar = () => {
//     if (sidebarOpen) {
//       closeAllSubMenus();
//     }
//     toggleSidebar(!sidebarOpen);
//   };

//   const isSubMenuSelected = (menu) => {
//     if (menu.submenus) {
//       return menu.submenus.some((submenu) => location.pathname === submenu.route);
//     }
//     return false;
//   };

//   return (
//     <Drawer
//       variant={mobileView ? "temporary" : "permanent"}
//       open={sidebarOpen}
//       onClose={handleToggleSidebar}
//       sx={{
//         "& .MuiDrawer-paper": {
//           width: sidebarOpen ? 300 : 60,
//           backgroundColor: "#424752",
//           color: "white",
//           transition: "width 0.3s",
//         },
//       }}
//     >
//       <div className="flex justify-end p-2">
//         <IconButton onClick={handleToggleSidebar}>
//           <MenuIcon style={{ color: "white" }} />
//         </IconButton>
//       </div>
//       <List className="hide-scrollbar">
//         {menus.map((menu, menuIndex) => (
//           <div key={menuIndex}>
//             <ListItem
//               button
//               onClick={() => (menu.submenus ? toggleSubMenu(menuIndex) : navigateTo(menu.route))}
//               selected={isCurrentRoute(menu.route) || isSubMenuSelected(menu)}
//               sx={{
//                 "&:hover": {
//                   backgroundColor: "#2a2f3b",
//                 },
//                 backgroundColor: isCurrentRoute(menu.route) || isSubMenuSelected(menu) ? "#2a2f3b" : "",
//               }}
//             >
//               <ListItemIcon sx={{ color: "white", minWidth: 55 }}>
//                 {menu.icon}
//               </ListItemIcon>
//               <ListItemText
//                 primary={menu.title}
//                 sx={{
//                   opacity: sidebarOpen ? 1 : 0,
//                   visibility: sidebarOpen ? "visible" : "hidden",
//                   transition: "opacity 0.3s, visibility 0.3s",
//                   width: sidebarOpen ? "auto" : 0,
//                   whiteSpace: "nowrap",
//                 }}
//               />
//               {menu.submenus && (menu.showSubMenu ? <ExpandLess /> : <ExpandMore />)}
//             </ListItem>
//             {menu.submenus && (
//               <Collapse in={menu.showSubMenu} timeout="auto" unmountOnExit>
//                 <List component="div" disablePadding className="hide-scrollbar">
//                   {menu.submenus.map((submenu, submenuIndex) => (
//                     <ListItem
//                       button
//                       key={submenuIndex}
//                       onClick={() => navigateTo(submenu.route)}
//                       sx={{
//                         pl: 4,
//                         "&:hover": {
//                           backgroundColor: "#2a2f3b",
//                         },
//                         backgroundColor: isCurrentRoute(submenu.route) ? "#2a2f3b" : "",
//                       }}
//                     >
//                       <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
//                         {submenu.icon}
//                       </ListItemIcon>
//                       <ListItemText
//                         primary={submenu.title}
//                         sx={{
//                           opacity: sidebarOpen ? 1 : 0,
//                           visibility: sidebarOpen ? "visible" : "hidden",
//                           transition: "opacity 0.3s, visibility 0.3s",
//                           width: sidebarOpen ? "auto" : 0,
//                           whiteSpace: "nowrap",
//                         }}
//                       />
//                     </ListItem>
//                   ))}
//                 </List>
//               </Collapse>
//             )}
//           </div>
//         ))}
//       </List>

//     </Drawer>
//   );
// };

// export default Sidebar;
